import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { format, isAfter, isBefore } from 'date-fns';
import { TableExportDirective } from 'src/app/shared/table-export/table-export.directive';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, firstValueFrom, interval } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { LinkedObjectsToFileWithId } from 'src/app/models/LinkedObjectsToFileWithId';
import YukiDocument from 'src/app/models/yuki/YukiDocument';
import YukiFolder from 'src/app/models/yuki/YukiFolder';
import { SettingsState } from 'src/app/settings/settings.state';
import { DeleteLinkedObject, GetAllObjectsLinked } from 'src/app/shared/global-files/global-files.actions';
import { GlobalFilesState } from 'src/app/shared/global-files/global-files.state';

import { LinkedObjectToFile } from '../../models/LinkedObjectToFile';
import { VAT } from '../../models/VAT';
import { roundNumber } from '../../shared/utils/number-utils';
import { YukiDetailModalComponent } from '../yuki-detail-modal/yuki-detail-modal.component';
import { FetchAllDocuments, FetchFolders, FlagYukiDocument, UpdateVisibilityYukiDocument, VerifyYukiDocument } from '../yuki.actions';
import { YukiService } from '../yuki.service';
import { YukiState } from '../yuki.state';

@Component({
    selector: 'app-yuki-documents-list',
    templateUrl: './yuki-documents-list.component.html',
    styleUrls: ['./yuki-documents-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class YukiDocumentsListComponent implements OnInit, OnDestroy {
    private static readonly INCLUDE_FLEET_STORAGE_KEY_PREFIX_BY_COMPANY_UUID = 'yuki-include-fleet-company:';
    private static readonly INCLUDE_FLEET_STORAGE_KEY_PREFIX_BY_SUPPLIER_NAME = 'yuki-include-fleet-supplier:';

    private subscriptions = new Subscription();
    private refreshingZeroAmountDocuments = false;
    private zeroAmountRefreshAttemptedByYukiId: Set<string> = new Set<string>();

    @Select(SettingsState.TableDisplayColumns('yuki-documents-list')) displayedColumns$: Observable<string[]>;
    @Select(YukiState.currentFolder) currentFolder$: Observable<YukiFolder>;
    @Select(YukiState.documentsForCurrentFolder) getDocumentsForCurrentFolder$: Observable<YukiDocument[]>;
    @Select(GlobalFilesState.LinkedObjects) linkedObjects$: Observable<LinkedObjectsToFileWithId[]>;

    filter: string;
    displayedColumns: string[] = [];
    allDisplayedColumns: string[] = [];
    dataSource: MatTableDataSource<YukiDocument> = new MatTableDataSource<YukiDocument>();

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(TableExportDirective, { static: true })
    exporter: TableExportDirective;

    totalAmount: number;
    totalAmountExcl: number;
    totalVATAmount: number;

    currentFolder: YukiFolder = null;
    documents: YukiDocument[] = null;

    private static readonly DEFAULT_LINKED_FILTER_LABEL: string = 'Filter on linked state';

    // transient AI status map keyed by document uuid (do not mutate document objects)
    private aiStatus: Map<string, string> = new Map<string, string>();
    // Selected documents for batch processing
    selectedDocuments: Set<string> = new Set<string>();

    defaultFilterLabel: string;

    filterLabel: string;
    searchLabel: string;
    visibleChecked = false;
    flaggedChecked = false;
    verifiedChecked = false;
    paidChecked = true;

    // selection for batch operations (checkboxes)
    selectedIds: Set<string> = new Set<string>();

    private readonly defaultStartDate: string = `${new Date().getFullYear()}-01-01`;
    private readonly salesDefaultStartDate: string = `${new Date().getFullYear()}-01-01`;
    startDate: string = this.defaultStartDate;
    endDate: string = format(new Date(), 'yyyy-MM-dd');
    linkedObjects: LinkedObjectsToFileWithId[];
    private linkedObjectsIndexByIdentifier: Record<string, LinkedObjectsToFileWithId> = {};
    private linkedObjectsByFileId: Record<string, LinkedObjectsToFileWithId> = {};
    bulkLinkingInProgress = false;
    bulkLinkJustCompleted = false;
    currentBulkLinkStatus: any = null;
    private bulkLinkStatusSubscription: Subscription;
    private bulkLinkCompletedTimeout: any;
    private pendingDocDeepLink: string | null = null;

    // Duplicate detection
    duplicateGroups: { key: string; contactName: string; date: string; amount: number; documents: YukiDocument[] }[] = [];
    duplicatesPanelVisible = false;
    duplicateMatchMode: 'amountDate' | 'amountDateContact' = 'amountDateContact';

    constructor(
        private store: Store,
        private modalService: NgbModal,
        private changeDetectorRef: ChangeDetectorRef,
        private yuki: YukiService,
        private toastr: ToastrService,
        private logger: NGXLogger,
        private route: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        this.filterLabel = YukiDocumentsListComponent.DEFAULT_LINKED_FILTER_LABEL;
        this.defaultFilterLabel = YukiDocumentsListComponent.DEFAULT_LINKED_FILTER_LABEL;

        // Read doc query param for deep-linking to a specific document
        const docParam = this.route.snapshot.queryParamMap.get('doc');
        if (docParam) {
            this.pendingDocDeepLink = docParam;
        }

        this.createMatTable();
        this.fetchData();
        this.resumeBulkLinkStatusIfRunning();
    }

    ngOnDestroy(): void {
        this.stopBulkLinkStatusPolling();
        this.subscriptions.unsubscribe();
        clearTimeout(this.bulkLinkCompletedTimeout);
    }

    private fetchData() {
        this.dispatchGetAllObjectsLinked();

        this.subscriptions.add(
            this.displayedColumns$.subscribe((columns) => {
                this.allDisplayedColumns = columns ? columns : YukiDocumentsListColumns.DEFAULT;
                this.displayedColumns = columns ? columns : YukiDocumentsListColumns.DEFAULT;
                this.filterDisplayedColumns();
            }),
        );

        this.subscriptions.add(
            this.currentFolder$.subscribe((folder) => {
                const previousFolderId = this.currentFolder?.id;
                this.currentFolder = folder;
                const folderChanged = previousFolderId !== folder?.id;

                if (folderChanged) {
                    if (this.isSalesFolder(folder)) {
                        this.startDate = this.salesDefaultStartDate;
                        this.endDate = format(new Date(), 'yyyy-MM-dd');
                        this.visibleChecked = true;
                        this.verifiedChecked = false;
                    } else {
                        this.startDate = this.defaultStartDate;
                        this.endDate = format(new Date(), 'yyyy-MM-dd');
                        this.visibleChecked = false;
                        this.verifiedChecked = false;
                    }
                    this.dispatchGetAllObjectsLinked();
                }

                // Only re-execute filter if folder changed and we have data
                if (folderChanged && this.documents?.length > 0) {
                    this.executeFilter();
                }
            }),
        );

        this.subscriptions.add(
            this.getDocumentsForCurrentFolder$.subscribe((documents) => {
                this.documents = documents;
                this.dataSource.data = this.documents;
                this.dataSource.sort = this.sort;
                this.dataSource.paginator = this.paginator;
                this.rebuildLinkedObjectsIndex();
                this.filterDisplayedColumns();
                this.executeFilter();
                this.refreshZeroAmountDocumentsInBackground(this.documents || []);
                this.tryOpenDeepLinkedDocument();
            }),
        );

        this.subscriptions.add(
            this.linkedObjects$.subscribe((linkedObjects) => {
                if (!linkedObjects) {
                    this.dispatchGetAllObjectsLinked();
                    return;
                }
                this.linkedObjects = linkedObjects;
                this.rebuildLinkedObjectsIndex();
            }),
        );
    }

    private dispatchGetAllObjectsLinked() {
        this.store.dispatch(new GetAllObjectsLinked(this.startDate, this.endDate));
    }

    private async refreshZeroAmountDocumentsInBackground(documents: YukiDocument[]): Promise<void> {
        if (this.refreshingZeroAmountDocuments || !documents?.length) {
            return;
        }

        const startingFolderId = this.currentFolder?.id ?? documents[0]?.folderId ?? 0;

        const candidates = documents.filter((document) => {
            const yukiId = (document?.yukiId || '').toString().trim();
            if (!yukiId || this.zeroAmountRefreshAttemptedByYukiId.has(yukiId)) {
                return false;
            }

            const archived = Boolean((document as any)?.archived ?? false);
            const isDeletedFolder = Number((document as any)?.folderId ?? 0) === -1;
            if (archived || isDeletedFolder) {
                return false;
            }

            const amount = Number((document as any)?.amount ?? 0);
            const amountExcl = Number((document as any)?.amountExcl ?? 0);
            const vatAmount = Number((document as any)?.vatamount ?? (document as any)?.vatAmount ?? 0);
            return amount === 0 && amountExcl === 0 && vatAmount === 0;
        });

        if (!candidates.length) {
            return;
        }

        this.refreshingZeroAmountDocuments = true;
        try {
            const chunkSize = 25;
            for (let index = 0; index < candidates.length; index += chunkSize) {
                const chunk = candidates.slice(index, index + chunkSize);
                await Promise.all(
                    chunk.map(async (document) => {
                        const refreshIdentifier = ((document as any)?.yukiId || (document as any)?.uuid || '').toString().trim();
                        const yukiId = (document?.yukiId || '').toString().trim();
                        if (!refreshIdentifier || !yukiId) return;

                        this.zeroAmountRefreshAttemptedByYukiId.add(yukiId);
                        try {
                            const refreshed = await firstValueFrom(this.yuki.refreshDocumentFromYukiAPI(refreshIdentifier));
                            if (refreshed) {
                                this.applyRefreshedFinancialsToRow(refreshed);
                            }
                        } catch (error) {
                            this.logger.debug(`Could not refresh financials for Yuki document ${yukiId}`, error);
                        }
                    }),
                );

                // spread backend load between chunks
                await new Promise((resolve) => setTimeout(resolve, 150));
                if (!this.refreshingZeroAmountDocuments) {
                    break;
                }
            }

            // Only reload documents if the user is still on the same folder
            const currentFolderId = this.currentFolder?.id;
            if (currentFolderId && currentFolderId === startingFolderId) {
                await firstValueFrom(this.store.dispatch(new FetchAllDocuments(currentFolderId)).pipe());
            }
        } finally {
            this.refreshingZeroAmountDocuments = false;
        }
    }

    private applyRefreshedFinancialsToRow(refreshed: YukiDocument): void {
        if (!refreshed || !this.documents?.length) {
            return;
        }

        const refreshedYukiId = (refreshed.yukiId || '').toString().trim();
        const refreshedUuid = (refreshed.uuid || '').toString().trim();

        const targetIndex = this.documents.findIndex((document) => {
            const documentYukiId = ((document as any)?.yukiId || '').toString().trim();
            const documentUuid = ((document as any)?.uuid || '').toString().trim();
            return (refreshedYukiId && documentYukiId === refreshedYukiId) || (refreshedUuid && documentUuid === refreshedUuid);
        });

        if (targetIndex < 0) {
            return;
        }

        const target = this.documents[targetIndex] as any;
        target.amount = Number((refreshed as any).amount ?? 0);
        target.amountExcl = Number((refreshed as any).amountExcl ?? 0);
        target.vatamount = Number((refreshed as any).vatamount ?? (refreshed as any).vatAmount ?? 0);
        target.vat = VAT.findVAT((refreshed as any).vattext ?? (refreshed as any).VATText ?? target.vattext);
        target.vattext = (refreshed as any).vattext ?? (refreshed as any).VATText ?? target.vattext;

        this.dataSource.data = [...this.documents];
    }

    private filterDisplayedColumns() {
        this.displayedColumns = this.allDisplayedColumns.filter((column) => this.isSalesFolder(this.currentFolder) || column !== 'invoiceNumber');
    }

    isSalesFolder(folder: YukiFolder): boolean {
        if (!folder) return false;
        const description = (folder.description || '').toLowerCase();
        return folder.id === 2 || description.includes('verkoop') || description.includes('sales');
    }

    get isCurrentFolderSales(): boolean {
        return this.isSalesFolder(this.currentFolder);
    }

    // Selection helpers
    isSelected(row: YukiDocument): boolean {
        const uuid = (row as any).uuid || (row as any).yukiId || null;
        if (!uuid) return false;
        return this.selectedIds.has(uuid);
    }

    toggleSelect(row: YukiDocument, $event?: Event) {
        if ($event) $event.stopPropagation();
        const uuid = (row as any).uuid || (row as any).yukiId || null;
        if (!uuid) return;
        if (this.selectedIds.has(uuid)) this.selectedIds.delete(uuid);
        else this.selectedIds.add(uuid);
    }

    areAllSelected(): boolean {
        const visible = this.dataSource.filteredData || [];
        if (visible.length === 0) return false;
        return visible.every((r) => {
            const u = (r as any).uuid || (r as any).yukiId || null;
            return u && this.selectedIds.has(u);
        });
    }

    toggleSelectAll($event?: Event) {
        if ($event) $event.stopPropagation();
        const visible = this.dataSource.filteredData || [];
        if (this.areAllSelected()) {
            visible.forEach((r) => {
                const u = (r as any).uuid || (r as any).yukiId || null;
                if (u) this.selectedIds.delete(u);
            });
        } else {
            visible.forEach((r) => {
                const u = (r as any).uuid || (r as any).yukiId || null;
                if (u) this.selectedIds.add(u);
            });
        }
    }

    /** Process selected invoices sequentially, one-by-one.
     * If vatOnly is true only VAT is updated.
     * If linkOnly is true only linking/sublinking is performed (no VAT update).
     */
    async processSelectedSequentially(vatOnly: boolean = false, linkOnly: boolean = false): Promise<void> {
        const ids = Array.from(this.selectedIds);
        for (const id of ids) {
            try {
                // find document by uuid or yukiId
                const doc = (this.documents || []).find((d) => (d as any).uuid === id || (d as any).yukiId === id);
                if (!doc) {
                    // attempt to refresh documents if missing
                    await firstValueFrom(this.store.dispatch(new FetchAllDocuments(this.currentFolder?.id || 0)).pipe());
                    const refreshed = this.documents || [];
                    // try again
                    // eslint-disable-next-line @typescript-eslint/no-shadow
                    const doc2 = (refreshed || []).find((d) => (d as any).uuid === id || (d as any).yukiId === id);
                    if (!doc2) continue;
                    await this.runAiForInvoice(doc2, vatOnly, linkOnly);
                } else {
                    await this.runAiForInvoice(doc, vatOnly, linkOnly);
                }
            } catch (e) {
                this.logger.debug('Processing selected invoice failed', e);
            }
        }
        // after processing, clear selection and refresh list
        this.selectedIds.clear();
        await firstValueFrom(this.store.dispatch(new FetchAllDocuments(this.currentFolder?.id || 0)).pipe());
    }

    // Convenience wrappers for the UI dropdown
    public async processSelectedVATOnly(): Promise<void> {
        await this.processSelectedSequentially(true);
    }

    public async processSelectedWithLinking(): Promise<void> {
        await this.processSelectedSequentially(false);
    }

    public async processSelectedLinkingOnly(): Promise<void> {
        await this.processSelectedSequentially(false, true);
    }

    public async linkAllUnlinkedInvoices(): Promise<void> {
        if (!this.currentFolder?.id || this.bulkLinkingInProgress) return;

        this.bulkLinkingInProgress = true;
        this.currentBulkLinkStatus = null;
        try {
            const status = await firstValueFrom(this.yuki.startAutoLinkUnlinkedInvoices(this.currentFolder.id, this.startDate, this.endDate));
            this.currentBulkLinkStatus = status;
            this.toastr.info(status?.message || 'Bulk auto-link started in background.');
            this.startBulkLinkStatusPolling();
        } catch (error) {
            this.logger.error('Bulk link unlinked invoices failed', error);
            this.toastr.error('Link all unlinked invoices failed.');
            this.bulkLinkingInProgress = false;
            this.currentBulkLinkStatus = null;
        }
    }

    get bulkLinkProgressLabel(): string {
        if (!this.bulkLinkingInProgress) {
            return 'Link all unlinked invoices';
        }

        const processed = this.currentBulkLinkStatus?.processedDocuments ?? 0;
        const total = this.currentBulkLinkStatus?.totalInScope ?? 0;
        if (total > 0) {
            return `Linking... ${processed}/${total}`;
        }
        return 'Linking...';
    }

    get bulkLinkPhaseLabel(): string {
        if (!this.currentBulkLinkStatus) {
            return 'Starting bulk link...';
        }
        if (this.bulkLinkJustCompleted && !this.bulkLinkingInProgress) {
            const failed = this.currentBulkLinkStatus?.failed ?? 0;
            if (failed > 0) {
                return 'Bulk link completed with errors';
            }
            return 'Bulk link completed!';
        }
        if (this.currentBulkLinkStatus?.totalInScope > 0) {
            return 'AI auto-linking in progress...';
        }
        return 'Preparing bulk link...';
    }

    get bulkLinkProgressPercent(): number {
        const total = this.currentBulkLinkStatus?.totalInScope ?? 0;
        const processed = this.currentBulkLinkStatus?.processedDocuments ?? 0;
        if (total === 0) {
            return 0;
        }
        return Math.round((processed / total) * 100);
    }

    private startBulkLinkStatusPolling(): void {
        this.stopBulkLinkStatusPolling();
        this.bulkLinkStatusSubscription = interval(5000)
            .pipe(
                startWith(0),
                switchMap(() => this.yuki.getAutoLinkUnlinkedInvoicesStatus()),
            )
            .subscribe({
                next: (status: any) => {
                    this.currentBulkLinkStatus = status;
                    if (status?.running) {
                        return;
                    }

                    this.stopBulkLinkStatusPolling();
                    this.bulkLinkingInProgress = false;

                    const linkedNow = status?.linkedNow ?? 0;
                    const failed = status?.failed ?? 0;
                    const skipped = status?.skipped ?? 0;
                    const unlinkedInScope = status?.unlinkedInScope ?? 0;
                    const linkedPercentage = unlinkedInScope > 0 ? Math.round((linkedNow / unlinkedInScope) * 100) : 0;

                    if (failed > 0) {
                        this.toastr.warning(
                            `Linked ${linkedNow}/${unlinkedInScope} unlinked invoices (${linkedPercentage}%). Skipped ${skipped}. Failed ${failed}.`,
                        );
                    } else {
                        this.toastr.success(
                            `Linked ${linkedNow}/${unlinkedInScope} unlinked invoices (${linkedPercentage}%). Skipped ${skipped}. Failed ${failed}.`,
                        );
                    }

                    // Keep panel visible briefly after completion
                    this.bulkLinkJustCompleted = true;
                    clearTimeout(this.bulkLinkCompletedTimeout);
                    this.bulkLinkCompletedTimeout = setTimeout(() => {
                        this.bulkLinkJustCompleted = false;
                        this.currentBulkLinkStatus = null;
                    }, 5000);

                    this.dispatchGetAllObjectsLinked();
                    this.store.dispatch(new FetchAllDocuments(this.currentFolder?.id));
                },
                error: (error) => {
                    this.logger.error('Bulk link status polling failed', error);
                    this.stopBulkLinkStatusPolling();
                    this.bulkLinkingInProgress = false;
                    this.currentBulkLinkStatus = null;
                    this.toastr.error('Could not fetch bulk-link status.');
                },
            });
    }

    private stopBulkLinkStatusPolling(): void {
        if (this.bulkLinkStatusSubscription) {
            this.bulkLinkStatusSubscription.unsubscribe();
            this.bulkLinkStatusSubscription = null;
        }
        if (!this.bulkLinkingInProgress) {
            this.currentBulkLinkStatus = null;
        }
    }

    public canShowBulkLinkButton(): boolean {
        const description = (this.currentFolder?.description || '').toLowerCase();
        return description.includes('aankoop') || description.includes('purchase') || description.includes('verkoop') || description.includes('sales');
    }

    private resumeBulkLinkStatusIfRunning(): void {
        this.yuki.getAutoLinkUnlinkedInvoicesStatus().subscribe({
            next: (status: any) => {
                if (!status) return;
                this.currentBulkLinkStatus = status;
                if (status.running) {
                    this.bulkLinkingInProgress = true;
                    this.startBulkLinkStatusPolling();
                }
            },
            error: () => {},
        });
    }

    private getDocumentIdentifiers(fileRef: any): string[] {
        if (!fileRef) return [];
        if (typeof fileRef === 'string' || typeof fileRef === 'number') {
            return [String(fileRef).trim()].filter(Boolean);
        }
        const row = fileRef as any;
        return [row?.uuid, row?.yukiId, row?.id]
            .map((value) => (value === null || value === undefined ? '' : String(value).trim()))
            .filter((value) => value.length > 0);
    }

    private registerLinkIndexKey(identifier: string, linked: LinkedObjectsToFileWithId): void {
        const trimmed = (identifier || '').trim();
        if (!trimmed) return;
        this.linkedObjectsIndexByIdentifier[trimmed] = linked;
        this.linkedObjectsIndexByIdentifier[trimmed.toLowerCase()] = linked;
        this.linkedObjectsByFileId[trimmed] = linked;
    }

    private rebuildLinkedObjectsIndex(): void {
        this.linkedObjectsIndexByIdentifier = {};
        this.linkedObjectsByFileId = {};

        (this.linkedObjects || []).forEach((linked) => {
            const linkedYukiId = linked?.yukiId === null || linked?.yukiId === undefined ? '' : String(linked.yukiId).trim();
            if (!linkedYukiId) return;
            this.registerLinkIndexKey(linkedYukiId, linked);
        });

        (this.documents || []).forEach((document) => {
            const documentYukiId = (document as any)?.yukiId === null || (document as any)?.yukiId === undefined ? '' : String((document as any).yukiId).trim();
            if (!documentYukiId) return;

            const linked = this.linkedObjectsIndexByIdentifier[documentYukiId] || this.linkedObjectsIndexByIdentifier[documentYukiId.toLowerCase()];
            if (!linked) return;

            this.getDocumentIdentifiers(document).forEach((identifier) => this.registerLinkIndexKey(identifier, linked));
        });
    }

    getLinksByFile(fileRef: any) {
        const identifiers = this.getDocumentIdentifiers(fileRef);
        if (!identifiers.length) return null;

        for (const id of identifiers) {
            const indexed = this.linkedObjectsIndexByIdentifier[id] || this.linkedObjectsIndexByIdentifier[id.toLowerCase()];
            if (indexed) return indexed;

            const cached = this.linkedObjectsByFileId[id];
            if (cached) return cached;
        }

        return null;
    }

    getNumberOfLinksByFile(fileRef: any): number {
        const linkedObjects: LinkedObjectToFile[] = this.getLinksByFile(fileRef)?.linkedObjects;
        return linkedObjects ? linkedObjects.length : 0;
    }

    getNumberOfSublinksByFile(fileRef: any): number {
        const linkedObjects: LinkedObjectToFile[] = this.getLinksByFile(fileRef)?.linkedObjects;
        return linkedObjects ? linkedObjects.reduce((total: number, linkedObject: LinkedObjectToFile) => total + linkedObject.childInvoiceLinks.length, 0) : 0;
    }

    isFullySublinked(fileRef: any): boolean {
        const linkedObjects: LinkedObjectToFile[] = this.getLinksByFile(fileRef)?.linkedObjects;
        if (!linkedObjects) return false;
        const linkedAmount: number = this.calculateLinkedAmount(linkedObjects);
        const sublinkedAmount: number = this.calculateSublinkedAmount(linkedObjects);
        return linkedAmount === sublinkedAmount;
    }

    isPartiallySublinked(fileRef: any): boolean {
        const linkedObjects: LinkedObjectToFile[] = this.getLinksByFile(fileRef)?.linkedObjects;
        if (!linkedObjects) return false;
        const linkedAmount: number = this.calculateLinkedAmount(linkedObjects);
        const sublinkedAmount: number = this.calculateSublinkedAmount(linkedObjects);
        return linkedAmount > sublinkedAmount && sublinkedAmount > 0;
    }

    isOverSublinked(fileRef: any): boolean {
        const linkedObjects: LinkedObjectToFile[] = this.getLinksByFile(fileRef)?.linkedObjects;
        if (!linkedObjects) return false;
        const linkedAmount: number = this.calculateLinkedAmount(linkedObjects);
        const sublinkedAmount: number = this.calculateSublinkedAmount(linkedObjects);
        return sublinkedAmount > linkedAmount;
    }

    private calculateLinkedAmount(linkedObjects: LinkedObjectToFile[]): number {
        return roundNumber(linkedObjects.reduce((total: number, linkedObject: LinkedObjectToFile) => total + linkedObject.price, 0));
    }

    private calculateSublinkedAmount(linkedObjects: LinkedObjectToFile[]): number {
        return roundNumber(
            linkedObjects.reduce(
                (total: number, linkedObject: LinkedObjectToFile) =>
                    total + linkedObject.childInvoiceLinks.reduce((total, childInvoiceLink) => total + childInvoiceLink.amount, 0),
                0,
            ),
        );
    }

    amountUsedByLinks(fileRef: any) {
        const linkedObjectsToFile: LinkedObjectsToFileWithId = this.getLinksByFile(fileRef);
        if (linkedObjectsToFile) {
            let total = 0;
            linkedObjectsToFile.linkedObjects.forEach((cil) => {
                total += cil.price;
            });
            return total;
        }
        return 0;
    }

    openLinkToObjectModal(event, yukiDocument: YukiDocument) {
        event.stopPropagation();

        const modalRef = this.modalService.open(YukiDetailModalComponent, {
            windowClass: 'modal-full-screen',
            animation: false,
        });
        modalRef.componentInstance.yukiDocument = yukiDocument;
        modalRef.componentInstance.folderId = this.currentFolder.id;
        modalRef.componentInstance.onLinkTab = true;
    }

    updateData() {
        this.store.dispatch(new FetchAllDocuments(this.currentFolder?.id));
    }

    private tryOpenDeepLinkedDocument(): void {
        if (!this.pendingDocDeepLink || !this.documents?.length) return;
        const targetYukiId = this.pendingDocDeepLink;
        this.pendingDocDeepLink = null; // only try once

        const doc = this.documents.find((d) => (d.yukiId || '').toString().trim() === targetYukiId || (d.uuid || '').toString().trim() === targetYukiId);
        if (doc) {
            // Small delay to let the view settle
            setTimeout(() => this.openDetailModal(doc), 300);
        }
    }

    openDetailModal(yukiDocument: YukiDocument): void {
        if (!yukiDocument) return;

        const resolvedDocument = this.resolveDocumentForDetail(yukiDocument);
        if (!resolvedDocument) {
            this.toastr.error('Could not resolve Yuki document for detail view.');
            return;
        }

        const modalRef = this.modalService.open(YukiDetailModalComponent, {
            windowClass: 'modal-full-screen',
            animation: false,
        });
        modalRef.componentInstance.yukiDocument = resolvedDocument;
        modalRef.componentInstance.folderId = this.currentFolder.id;
    }

    /**
     * Find duplicate invoices within the currently visible (filtered) documents.
     * Two invoices are considered duplicates when they share the same document
     * date and the same total amount. When duplicateMatchMode is 'amountDateContact'
     * the contact name must also match.
     */
    findDuplicates(): void {
        const docs = (this.dataSource?.filteredData || []) as YukiDocument[];
        const groups = new Map<string, YukiDocument[]>();

        for (const doc of docs) {
            if (!doc) continue;
            const amount = Number((doc as any).amount ?? 0);
            if (!amount) continue; // skip 0-amount placeholders
            const dateRaw = (doc as any).documentDate;
            if (!dateRaw) continue;
            const dateKey = format(new Date(dateRaw), 'yyyy-MM-dd');
            const amountKey = amount.toFixed(2);
            const contactKey = (doc.contactName || '').trim().toLowerCase();

            const key = this.duplicateMatchMode === 'amountDateContact' ? `${dateKey}|${amountKey}|${contactKey}` : `${dateKey}|${amountKey}`;

            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(doc);
        }

        this.duplicateGroups = Array.from(groups.entries())
            .filter(([, list]) => list.length > 1)
            .map(([key, list]) => ({
                key,
                contactName: list[0].contactName || '',
                date: format(new Date(list[0].documentDate), 'yyyy-MM-dd'),
                amount: Number((list[0] as any).amount ?? 0),
                documents: list,
            }))
            .sort((a, b) => (a.date < b.date ? 1 : -1));

        this.duplicatesPanelVisible = true;
        if (this.duplicateGroups.length === 0) {
            this.toastr.info('Geen dubbele facturen gevonden in de huidige selectie.');
        } else {
            const totalDuplicates = this.duplicateGroups.reduce((sum, g) => sum + g.documents.length, 0);
            this.toastr.success(`${this.duplicateGroups.length} groep(en) met ${totalDuplicates} mogelijke dubbele facturen gevonden.`);
        }
    }

    closeDuplicatesPanel(): void {
        this.duplicatesPanelVisible = false;
        this.duplicateGroups = [];
    }

    setDuplicateMatchMode(mode: 'amountDate' | 'amountDateContact'): void {
        this.duplicateMatchMode = mode;
        if (this.duplicatesPanelVisible) {
            this.findDuplicates();
        }
    }

    getDocumentTitle(yukiDocument: YukiDocument): string {
        if (!yukiDocument) return '';
        return yukiDocument.subject || yukiDocument.fileName || yukiDocument.yukiId || yukiDocument.uuid || '';
    }

    private resolveDocumentForDetail(yukiDocument: YukiDocument): YukiDocument | null {
        if (!yukiDocument) return null;
        if (yukiDocument.uuid) return yukiDocument;

        const yukiId = (yukiDocument.yukiId || '').toString().trim();
        if (!yukiId) return yukiDocument;

        return (
            (this.documents || []).find(
                (document) => (document.yukiId || '').toString().trim() === yukiId || (document.uuid || '').toString().trim() === yukiId,
            ) || yukiDocument
        );
    }

    /** Headless AI flow for a single invoice row via backend: VAT -> allocation links. */
    async runAiForInvoice(yukiDocument: YukiDocument, vatOnly: boolean = false, linkOnly: boolean = false): Promise<void> {
        if (!yukiDocument) return;

        let resolvedDocument: YukiDocument = yukiDocument;
        if (!resolvedDocument?.linkedContact?.uuid && yukiDocument?.uuid) {
            try {
                resolvedDocument = await firstValueFrom(this.yuki.fetchDocumentById(yukiDocument.uuid));
            } catch (error) {
                this.logger.debug('Could not refresh Yuki document before AI allocation', error);
            }
        }

        const uuid = (resolvedDocument as any)?.uuid || (yukiDocument as any)?.uuid || (yukiDocument as any)?.yukiId || null;
        const includeFleet = this.readIncludeFleetPreferenceForDocument(resolvedDocument || yukiDocument);
        const aiHint = await this.readAiHintForDocument(resolvedDocument || yukiDocument);
        try {
            if (uuid) this.aiStatus.set(uuid, linkOnly ? 'linking' : 'VAT');
            this.changeDetectorRef.detectChanges();

            const response = await firstValueFrom(
                this.yuki.autoAllocateDocumentWithContext((resolvedDocument || yukiDocument).uuid, {
                    vatOnly,
                    linkOnly,
                    includeFleet,
                    aiHint,
                }),
            );

            console.info('[Yuki AI] Volledige vraag (prompt):', response?.aiPrompt || '(leeg)');
            console.info('[Yuki AI] Volledig antwoord:', response?.aiResponse || '(leeg)');
            console.info('[Yuki AI] includeFleet (lijstknop):', includeFleet);
            console.info('[Yuki AI] aiHint (lijstknop):', aiHint || '(leeg)');

            if (uuid) this.aiStatus.set(uuid, 'done');
            this.changeDetectorRef.detectChanges();

            if (response?.vatUpdated && !linkOnly) {
                this.toastr.success('VAT updated from AI.');
            }

            if (!vatOnly && response?.allocations?.length) {
                this.toastr.success('AI allocation links created.');
            } else if (!vatOnly) {
                const reason = (response?.noMatchReason || '').trim();
                console.info('[Yuki AI] Geen links gemaakt voor document', {
                    documentUuid: yukiDocument?.uuid,
                    reason: reason || 'Geen reden ontvangen',
                });
                this.toastr.info(reason ? `AI kon geen links maken: ${reason}` : 'AI kon geen links maken.');
            }

            await firstValueFrom(this.store.dispatch(new GetAllObjectsLinked(this.startDate, this.endDate)));
            await firstValueFrom(this.store.dispatch(new FetchAllDocuments(this.currentFolder?.id || 0)).pipe());
            if (uuid) this.aiStatus.delete(uuid);
        } catch (err) {
            this.logger.error('runAiForInvoice failed', err);
            this.toastr.error('AI invoice processing failed.');
            if (uuid) this.aiStatus.delete(uuid);
        }
    }

    private normalizeSupplierName(value: string): string {
        return (value || '').trim().toLowerCase().replace(/\s+/g, ' ');
    }

    private getIncludeFleetStorageKeyByCompanyUuid(companyUuid: string): string {
        return `${YukiDocumentsListComponent.INCLUDE_FLEET_STORAGE_KEY_PREFIX_BY_COMPANY_UUID}${companyUuid}`;
    }

    private getIncludeFleetStorageKeyBySupplierName(supplierName: string): string {
        return `${YukiDocumentsListComponent.INCLUDE_FLEET_STORAGE_KEY_PREFIX_BY_SUPPLIER_NAME}${this.normalizeSupplierName(supplierName)}`;
    }

    private readIncludeFleetPreferenceForDocument(yukiDocument: YukiDocument): boolean {
        const linkedContact = yukiDocument?.linkedContact as any;
        const companyUuid = (linkedContact?.uuid || '').trim();
        const supplierName = ((linkedContact?.title || linkedContact?.name || yukiDocument?.contactName || '') as string).trim();

        try {
            if (companyUuid) {
                const byCompany = localStorage.getItem(this.getIncludeFleetStorageKeyByCompanyUuid(companyUuid));
                if (byCompany !== null) {
                    return byCompany === 'true';
                }
            }

            if (supplierName) {
                const bySupplier = localStorage.getItem(this.getIncludeFleetStorageKeyBySupplierName(supplierName));
                if (bySupplier !== null) {
                    return bySupplier === 'true';
                }
            }
        } catch (error) {
            this.logger.debug('Could not read include-fleet preference for list AI button', error);
        }

        return false;
    }

    private async readAiHintForDocument(yukiDocument: YukiDocument): Promise<string | null> {
        const linkedContact = yukiDocument?.linkedContact as any;
        const companyUuid = (linkedContact?.uuid || '').trim();
        if (!companyUuid) {
            return null;
        }

        try {
            const logItems = await firstValueFrom(this.yuki.getLogbookItemsForObject(companyUuid));
            const hit = (logItems || []).find((item: any) => (item?.category || '').toString().trim().toLowerCase() === 'ai-classifier');
            const description = (hit?.description || '').toString().trim();
            return description || null;
        } catch (error) {
            this.logger.debug('Could not read ai-classifier hint for list AI button', error);
            return null;
        }
    }

    getAiStatus(row: YukiDocument): string | null {
        const uuid = (row as any).uuid || (row as any).yukiId || null;
        if (!uuid) return null;
        return this.aiStatus.get(uuid) || null;
    }

    getAiTooltip(row: YukiDocument): string {
        const status = this.getAiStatus(row);
        return status ? `AI: ${status}` : 'AI: extract VAT and create allocation links (auto)';
    }

    async clearAllocationsForInvoice(yukiDocument: YukiDocument): Promise<void> {
        if (!yukiDocument) {
            return;
        }

        const linked = this.getLinksByFile(yukiDocument);
        const links = linked?.linkedObjects || [];
        if (!links.length) {
            this.toastr.info('No allocation links to clear for this invoice.');
            return;
        }

        const confirmed = window.confirm(`Clear all allocation links for this invoice (${links.length})?`);
        if (!confirmed) {
            return;
        }

        try {
            for (const link of links) {
                await firstValueFrom(this.store.dispatch(new DeleteLinkedObject(yukiDocument.uuid, link.objectId)));
            }

            this.toastr.success('Allocation links cleared for invoice.');
            await firstValueFrom(this.store.dispatch(new GetAllObjectsLinked(this.startDate, this.endDate)));
            await firstValueFrom(this.store.dispatch(new FetchAllDocuments(this.currentFolder?.id || 0)));
        } catch (error) {
            this.logger.error('Failed to clear allocation links for invoice', error);
            this.toastr.error('Failed to clear allocation links.');
        }
    }

    getLinkTypeLabel(objectType: string): string {
        const normalized = (objectType || '').toString().trim().toLowerCase();
        if (normalized === 'project') return 'Project';
        if (normalized === 'budget') return 'Budget';
        if (normalized === 'employee') return 'Employee';
        if (normalized === 'overheadcategory') return 'Overhead';
        return objectType || 'Link';
    }

    applyFilter(filterValue) {
        this.searchLabel = filterValue.trim();
        this.executeFilter();
    }

    executeFilter() {
        this.dataSource.filter = JSON.stringify({
            searchResult: this.searchLabel?.trim().toLowerCase(),
            selectedLinkType: this.filterLabel.trim(),
            flaggedChecked: this.flaggedChecked,
            visibleChecked: this.visibleChecked,
            verifiedChecked: this.verifiedChecked,
            paidChecked: this.paidChecked,
            startDate: this.startDate,
            endDate: this.endDate,
        });

        this.totalAmount = this.totalAmountExcl = this.totalVATAmount = 0;
        this.dataSource.filteredData.forEach((document) => {
            this.totalAmount += document.amount;
            this.totalAmountExcl += document.amountExcl;
            this.totalVATAmount += document.vatamount;
        });
    }

    applyFilterLinked($event) {
        this.filterLabel = $event.target.title;
        this.executeFilter();
    }

    private createMatTable() {
        function roundedAmount(amount: number): number {
            return Math.round(amount * 100) / 100;
        }

        this.dataSource.filterPredicate = function (data, filter) {
            const filterObj = JSON.parse(filter);
            const documentDate = data?.documentDate ? new Date(data.documentDate as any) : null;
            const startDate = filterObj?.startDate ? new Date(filterObj.startDate) : null;
            const endDate = filterObj?.endDate ? new Date(filterObj.endDate) : null;

            const hasValidDocumentDate = !!documentDate && !Number.isNaN(documentDate.getTime());
            const hasValidStartDate = !!startDate && !Number.isNaN(startDate.getTime());
            const hasValidEndDate = !!endDate && !Number.isNaN(endDate.getTime());

            if (hasValidEndDate) {
                endDate.setHours(23, 59, 59, 999);
            }

            const isWithinDateRange =
                !hasValidDocumentDate || ((!hasValidStartDate || !isBefore(documentDate, startDate)) && (!hasValidEndDate || !isAfter(documentDate, endDate)));

            const totalAmount = Number.isFinite(Number((data as any)?.totalAmount)) ? Number((data as any).totalAmount) : 0;
            const amountExcl = Number.isFinite(Number((data as any)?.amountExcl)) ? Number((data as any).amountExcl) : 0;
            const isVisibleByDefault = data?.visible !== false;

            return (
                isWithinDateRange &&
                (filterObj.flaggedChecked ? data.flagged : true) &&
                (filterObj.visibleChecked ? true : isVisibleByDefault) &&
                (filterObj.verifiedChecked ? true : !data.verified) &&
                (filterObj.paidChecked ? true : !data.paid) &&
                (!filterObj.searchResult ||
                    data.invoiceNumber?.toLowerCase().includes(filterObj.searchResult?.toLowerCase()) ||
                    data.subject?.toLowerCase().includes(filterObj.searchResult?.toLowerCase()) ||
                    data.fileName?.toLowerCase().includes(filterObj.searchResult?.toLowerCase()) ||
                    data.contactName?.toLowerCase().includes(filterObj.searchResult?.toLowerCase())) &&
                ((filterObj.selectedLinkType == 'Partially linked' &&
                    roundedAmount(amountExcl) != roundedAmount(totalAmount) &&
                    roundedAmount(totalAmount) < roundedAmount(amountExcl) &&
                    roundedAmount(totalAmount) != 0) ||
                    (filterObj.selectedLinkType == 'Not linked' && roundedAmount(totalAmount) == 0) ||
                    (filterObj.selectedLinkType == 'Full linked' && roundedAmount(totalAmount) == roundedAmount(amountExcl)) ||
                    filterObj.selectedLinkType == YukiDocumentsListComponent.DEFAULT_LINKED_FILTER_LABEL ||
                    (filterObj.selectedLinkType == 'Over linked' && roundedAmount(totalAmount) > roundedAmount(amountExcl) && roundedAmount(data.amount) > 0) ||
                    (roundedAmount(amountExcl) < 0 && roundedAmount(totalAmount) < roundedAmount(amountExcl)))
            );
        };
    }

    flagDocument(yukiId: string) {
        this.store.dispatch(new FlagYukiDocument(yukiId));
    }

    hideDocument(yukiId: string) {
        this.store.dispatch(new UpdateVisibilityYukiDocument(yukiId));
    }

    verifyDocument(yukiDocument: YukiDocument) {
        if (!yukiDocument) return;
        const yukiId = (yukiDocument as any).yukiId || (yukiDocument as any).uuid || null;
        if (!yukiId) {
            this.toastr.error('Document id not available for verify.');
            return;
        }

        this.store.dispatch(new VerifyYukiDocument(yukiId)).subscribe({
            next: () => {
                // reuse same behavior as detail modal: toast + refresh lists
                this.toastr.success('Yuki document successfully verified.');
                this.store.dispatch(new FetchAllDocuments(this.currentFolder?.id || 0));
                this.dispatchGetAllObjectsLinked();
            },
            error: (err) => {
                this.logger.error('Failed to verify document', err);
                this.toastr.error('Failed to verify document.');
            },
        });
    }

    onDateRangeChanged() {
        this.executeFilter();
        this.dispatchGetAllObjectsLinked();
        this.store.dispatch(new FetchFolders(this.startDate, this.endDate));
    }

    applyFilterVisible() {
        this.visibleChecked = !this.visibleChecked;
        this.executeFilter();
    }

    applyFilterFlagged() {
        this.flaggedChecked = !this.flaggedChecked;
        this.executeFilter();
    }

    applyFilterVerified() {
        this.verifiedChecked = !this.verifiedChecked;
        this.executeFilter();
    }

    applyFilterPaid() {
        this.paidChecked = !this.paidChecked;
        this.executeFilter();
    }

    getAllColumns() {
        return YukiDocumentsListColumns.ALL;
    }

    getDefaultColumns() {
        return YukiDocumentsListColumns.DEFAULT;
    }

    roundedAmount(amount: number): number {
        return Math.round(amount * 100) / 100;
    }

    /**
     * Analyze invoice numbers for missing and duplicate numbers (only for sales folder)
     * Returns analysis results
     */
    analyzeInvoiceNumbers(): { missing: string[]; duplicates: string[]; hasIssues: boolean; year: string } {
        // Only analyze for sales folder
        if (!this.isSalesFolder(this.currentFolder)) {
            return { missing: [], duplicates: [], hasIssues: false, year: '' };
        }

        // Find the most recent year in the invoices
        let targetYear = '';
        const yearCounts = new Map<string, number>();

        (this.documents || []).forEach((doc) => {
            if (doc.invoiceNumber) {
                const invoiceNumStr = doc.invoiceNumber.toString();
                const yearMatch = invoiceNumStr.match(/^(\d{4})/);
                if (yearMatch) {
                    const year = yearMatch[1];
                    yearCounts.set(year, (yearCounts.get(year) || 0) + 1);
                }
            }
        });

        // Use the most recent year that has invoices
        if (yearCounts.size > 0) {
            targetYear = Array.from(yearCounts.keys()).sort().reverse()[0];
        } else {
            return { missing: [], duplicates: [], hasIssues: false, year: '' };
        }

        const invoiceNumbers: string[] = [];

        console.log('=== INVOICE ANALYSIS START ===');
        console.log('Target year:', targetYear);
        console.log('Total documents:', this.documents?.length);
        console.log('Years found:', Array.from(yearCounts.entries()));

        // Get all invoice numbers from target year
        (this.documents || []).forEach((doc) => {
            if (doc.invoiceNumber) {
                const invoiceNumStr = doc.invoiceNumber.toString();
                if (invoiceNumStr.startsWith(targetYear)) {
                    console.log('Adding invoice:', invoiceNumStr);
                    invoiceNumbers.push(invoiceNumStr);
                }
            }
        });

        console.log('Filtered invoice numbers for year', targetYear, ':', invoiceNumbers);

        if (invoiceNumbers.length === 0) {
            return { missing: [], duplicates: [], hasIssues: false, year: targetYear };
        }

        // Sort invoice numbers
        invoiceNumbers.sort();
        console.log('Sorted invoice numbers:', invoiceNumbers);

        // Find duplicates
        const duplicates: string[] = [];
        const seen = new Set<string>();
        invoiceNumbers.forEach((num) => {
            console.log('Checking number:', num, 'Already seen?', seen.has(num));
            if (seen.has(num)) {
                if (!duplicates.includes(num)) {
                    console.log('DUPLICATE FOUND:', num);
                    duplicates.push(num);
                }
            }
            seen.add(num);
        });

        console.log('Final duplicates array:', duplicates);

        // Find missing numbers
        const missing: string[] = [];
        const uniqueNumbers = Array.from(seen).sort();

        console.log('Unique numbers from Set:', uniqueNumbers);

        if (uniqueNumbers.length > 0) {
            // Extract numeric parts (the sequential part after the year)
            const numbers = uniqueNumbers
                .map((num) => {
                    // Extract just the sequential part (last 4 digits typically)
                    const match = num.match(/(\d{4})(\d+)$/);
                    const result = match ? parseInt(match[2], 10) : null;
                    console.log('Extracting from', num, 'Match:', match, 'Result:', result);
                    return result;
                })
                .filter((n) => n !== null);

            console.log('Extracted sequential numbers:', numbers);

            if (numbers.length > 0) {
                const min = Math.min(...numbers);
                const max = Math.max(...numbers);

                console.log('Range: min =', min, 'max =', max);

                // Check for missing numbers in range
                for (let i = min; i <= max; i++) {
                    const expectedNumber = `${targetYear}${i.toString().padStart(4, '0')}`;
                    if (!seen.has(expectedNumber)) {
                        console.log('MISSING:', expectedNumber);
                        missing.push(expectedNumber);
                    }
                }
            }
        }

        console.log('Final missing array:', missing);
        console.log('=== INVOICE ANALYSIS END ===');

        return {
            missing,
            duplicates,
            hasIssues: missing.length > 0 || duplicates.length > 0,
            year: targetYear,
        };
    }

    /**
     * Check if we should show invoice number analysis (only for sales folder)
     */
    shouldShowInvoiceAnalysis(): boolean {
        return this.isSalesFolder(this.currentFolder);
    }

    /**
     * Get the current year for display in the template
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }
}

export class YukiDocumentsListColumns {
    static readonly VERIFIED = 'verified';
    static readonly SELECT = 'select';
    static readonly SUBJECT = 'subject';
    static readonly AMOUNT = 'amount';
    static readonly CONTACT_NAME = 'contactName';
    static readonly DOCUMENT_DATE = 'documentDate';
    static readonly LINKED_TO = 'linkedTo';
    static readonly SUBLINKS = 'sublinks';
    static readonly EDIT = 'edit';
    static readonly INVOICE_NUMBER = 'invoiceNumber';

    static readonly ALL = [
        this.SELECT,
        this.VERIFIED,
        this.INVOICE_NUMBER,
        this.SUBJECT,
        this.AMOUNT,
        this.CONTACT_NAME,
        this.DOCUMENT_DATE,
        this.LINKED_TO,
        this.SUBLINKS,
        this.EDIT,
    ];
    static readonly DEFAULT = [
        this.SELECT,
        this.VERIFIED,
        this.INVOICE_NUMBER,
        this.SUBJECT,
        this.AMOUNT,
        this.CONTACT_NAME,
        this.DOCUMENT_DATE,
        this.LINKED_TO,
        this.SUBLINKS,
        this.EDIT,
    ];
}
